// Login Page
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Mail, Lock, AlertCircle, Globe, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

export default function LoginPage() {
  const { login, loginWithGoogle, error } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState('');

  // Change password state
  const [changeDialogOpen, setChangeDialogOpen] = useState(false);
  const [changeForm, setChangeForm] = useState({ current: '', new: '', confirm: '' });
  const [showChangeCurrent, setShowChangeCurrent] = useState(false);
  const [showChangeNew, setShowChangeNew] = useState(false);
  const [changing, setChanging] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLocalError('');
    
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setLocalError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!changeForm.current || !changeForm.new) {
      toast.error(language === 'el' ? 'Συμπληρώστε όλα τα πεδία' : 'Fill in all fields');
      return;
    }
    if (changeForm.new.length < 6) {
      toast.error(language === 'el' ? 'Ο νέος κωδικός πρέπει να έχει τουλάχιστον 6 χαρακτήρες' : 'New password must be at least 6 characters');
      return;
    }
    if (changeForm.new !== changeForm.confirm) {
      toast.error(language === 'el' ? 'Οι κωδικοί δεν ταιριάζουν' : 'Passwords do not match');
      return;
    }
    setChanging(true);
    try {
      await api.post('/auth/change-password', {
        current_password: changeForm.current,
        new_password: changeForm.new
      });
      toast.success(language === 'el' ? 'Ο κωδικός άλλαξε!' : 'Password changed!');
      setChangeDialogOpen(false);
      setChangeForm({ current: '', new: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'el' ? 'Αποτυχία αλλαγής' : 'Change failed'));
    } finally {
      setChanging(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-[#E6007E] to-[#b8005f]">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{ 
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_facilitate-score/artifacts/58269d0v_IMG_9526.jpg')`
          }}
        />
        <div className="relative z-10 flex flex-col justify-center p-12 text-white">
          <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-8">
            <span className="text-3xl font-bold">G</span>
          </div>
          <h1 className="font-heading text-4xl font-bold mb-4">
            GrowPlay Facilitator Hub
          </h1>
          <p className="text-xl text-white/80 mb-8">
            {language === 'el' 
              ? 'Σύστημα αξιολόγησης και κινήτρων για facilitators'
              : 'Evaluation and motivation system for facilitators'
            }
          </p>
          <div className="flex gap-4 text-sm text-white/60">
            <span>Summit.gr</span>
            <span>•</span>
            <span>GrowPlay®</span>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[#F8F9FA]">
        <div className="w-full max-w-md">
          {/* Language Toggle */}
          <div className="flex justify-end mb-6">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={toggleLanguage}
              className="text-gray-500 hover:text-[#E6007E]"
              data-testid="language-toggle"
            >
              <Globe size={18} className="mr-2" />
              {language === 'el' ? 'English' : 'Ελληνικά'}
            </Button>
          </div>

          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-[#E6007E] rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-xl">G</span>
            </div>
            <div>
              <h1 className="font-heading font-bold text-[#2A1514]">GrowPlay</h1>
              <p className="text-sm text-gray-500">Facilitator Hub</p>
            </div>
          </div>

          <Card className="border-0 shadow-xl">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="font-heading text-2xl text-[#2A1514]">
                {t('login')}
              </CardTitle>
              <CardDescription>
                {language === 'el' 
                  ? 'Συνδεθείτε στον λογαριασμό σας'
                  : 'Sign in to your account'
                }
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {(error || localError) && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error || localError}</AlertDescription>
                </Alert>
              )}

              {/* Google Login */}
              <Button 
                variant="outline" 
                className="w-full h-12 border-gray-200 hover:bg-gray-50 hover:border-[#E6007E]/30"
                onClick={loginWithGoogle}
                data-testid="google-login-btn"
              >
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {t('loginWithGoogle')}
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <Separator className="w-full" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-gray-500">{t('orContinueWith')}</span>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">{t('email')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <Input
                      id="email"
                      type="email"
                      placeholder="name@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 h-12"
                      required
                      data-testid="email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">{t('password')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 pr-10 h-12"
                      required
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      data-testid="toggle-password-visibility"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-12 bg-[#E6007E] hover:bg-[#b8005f] text-white font-medium"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? t('loading') : t('signIn')}
                </Button>
              </form>

              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={() => setChangeDialogOpen(true)}
                  className="text-[#E6007E] hover:underline font-medium"
                  data-testid="change-password-link"
                >
                  {language === 'el' ? 'Αλλαγή κωδικού' : 'Change password'}
                </button>
                <Link to="/register" className="text-[#E6007E] hover:underline font-medium" data-testid="register-link">
                  {t('signUp')}
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Change Password Dialog */}
      <Dialog open={changeDialogOpen} onOpenChange={setChangeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">
              {language === 'el' ? 'Αλλαγή Κωδικού' : 'Change Password'}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-500">
            {language === 'el'
              ? 'Πρέπει να είστε συνδεδεμένοι για να αλλάξετε κωδικό. Συνδεθείτε πρώτα.'
              : 'You must be logged in to change your password. Log in first.'
            }
          </p>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>{language === 'el' ? 'Τρέχων κωδικός' : 'Current password'}</Label>
              <div className="relative">
                <Input
                  type={showChangeCurrent ? 'text' : 'password'}
                  value={changeForm.current}
                  onChange={(e) => setChangeForm({...changeForm, current: e.target.value})}
                  className="pr-10"
                  data-testid="current-password-input"
                />
                <button type="button" onClick={() => setShowChangeCurrent(!showChangeCurrent)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showChangeCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>{language === 'el' ? 'Νέος κωδικός' : 'New password'}</Label>
              <div className="relative">
                <Input
                  type={showChangeNew ? 'text' : 'password'}
                  value={changeForm.new}
                  onChange={(e) => setChangeForm({...changeForm, new: e.target.value})}
                  className="pr-10"
                  data-testid="new-password-input"
                />
                <button type="button" onClick={() => setShowChangeNew(!showChangeNew)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showChangeNew ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>{language === 'el' ? 'Επιβεβαίωση νέου κωδικού' : 'Confirm new password'}</Label>
              <Input
                type={showChangeNew ? 'text' : 'password'}
                value={changeForm.confirm}
                onChange={(e) => setChangeForm({...changeForm, confirm: e.target.value})}
                data-testid="confirm-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangeDialogOpen(false)}>{t('cancel')}</Button>
            <Button
              onClick={handleChangePassword}
              className="bg-[#E6007E] hover:bg-[#b8005f]"
              disabled={changing}
              data-testid="change-password-submit"
            >
              {changing
                ? (language === 'el' ? 'Αλλαγή...' : 'Changing...')
                : (language === 'el' ? 'Αλλαγή κωδικού' : 'Change password')
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
